// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Redux & Store & Actions
import { useDispatch } from "react-redux";
import { getGallery } from "store/modules/main-pages/galleries/get-gallery.action";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardFooter } from "components/layout/Card/CardFooter";
import LoadingContainer from "components/layout/LoadingContainer";
import ShowMoreLink from "components/layout/ShowMoreLink";
import Heading from "components/typography/HeadingLink";
import Masonry from "components/layout/Masonry/Masonry";
import MasonryVideo from "components/layout/Masonry/MasonryVideo";
import MasonryImage from "components/layout/Masonry/MasonryImage";

// ** Custom Hooks
import useImagesCustomMaxWidth from "hooks/useImagesCustomMaxWidth";

// ** Utils & Helpers
import generatePath from "utils/generatePath";

// **Routes
import containers from "containers";

// #####################################################

const columns = {
	phone: 3,
	tablet: 3,
	desktop: 4,
	giant: 4,
};

// #####################################################

const GalleryMasonry = ({
	gallery,
	loading,
	isReload,
	locationSearch,
	locationFull,
}) => {
	const dispatch = useDispatch();

	const { t } = useTranslation(["common", "pages"]);

	const to = generatePath(containers.gallery.link);

	const customMaxWidth = useImagesCustomMaxWidth({ isHomeCard: false });

	const items = gallery.map((media) => {
		if (media.media_type === "photo") {
			return (
				<MasonryImage
					key={media.id}
					contentId={media.id}
					contentAlbum={media.album}
					contentType={media.type}
					contentHeight={media.height}
					contentWidth={media.width}
					contentLikesCount={media.likes}
					contentCommentsCount={media.comments}
					imageVert={media?.images?.vert}
					locationSearch={locationSearch}
					userId={media.user_id}
					userLogin={media.login}
					userAvatar={media.avatar}
					userOnline={media.online}
					userStatusVip={media.status_vip}
					likes={media.likes}
				/>
			);
		}

		return (
			<MasonryVideo
				key={media.id}
				contentId={media.id}
				contentAlbum={media.album}
				contentType={media.type}
				contentHeight={media.height}
				contentWidth={media.width}
				contentLikesCount={media.likes}
				contentCommentsCount={media.comments}
				contentPreviewVertical={media.thumbs?.preview_vertical}
				contentSourceFull={media.video}
				contentPosterVertical={media.thumbs?.poster_vertical}
				locationSearch={locationSearch}
				userId={media.user_id}
				userLogin={media.login}
				userAvatar={media.avatar}
				userOnline={media.online}
				userStatusVip={media.status_vip}
				likes={media.likes}
			/>
		);
	});

	const handleShowMore = () => {
		dispatch(
			getGallery({
				filters: {
					location: locationFull,
					period: {
						selectedDate: {
							year: new Date().getFullYear(),
							month: 0,
						},
						period: "latest",
					},
				},
				siteName: "galleriesAll",
			})
		);
	};

	// #####################################################

	return (
		<CardWrapper>
			<CardTitle>
				<Heading to={to} type="h3" onClick={handleShowMore}>
					{t("pages:galleries")}
				</Heading>
			</CardTitle>

			<CardBody $customPaddingInMobile={"0.75rem 3px"}>
				<LoadingContainer
					empty={gallery.length === 0}
					noContentText={t("common:errors:noResults")}
					loading={loading || isReload}
					loaderDefault
				>
					{!loading && (
						<Masonry
							customMaxWidth={customMaxWidth}
							columns={columns}
						>
							{items}
						</Masonry>
					)}
				</LoadingContainer>
			</CardBody>

			<CardFooter>
				<ShowMoreLink to={to} onClick={handleShowMore} />
			</CardFooter>
		</CardWrapper>
	);
};

// #####################################################

export default GalleryMasonry;

// #####################################################
